import { UseMutateAsyncFunction, useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router"
import { TriggerToast } from "../../components/globals/Toaster"
import { SELECTED_WORKSPACE_KEY } from "../../constants"
import { useAxios } from "../../contexts/AxiosContext"
import { ExperimentStatus, ExperimentType } from "@prismancy/shared"
import { StorageService } from "../../services"
import { AudienceRules, Experiment, PageRules, RedirectSettings } from "../../types"
import useObjectives from "./useObjectives"
import { appRoutes } from "../../routes/appRoutes"
import { registerCreatedExperiment } from "../../utils/queryInvalidation"

type CreateExperimentParams = {
  activation_event: string
  hypothesis: string
  name: string
  objective_id: number
  status: string
  traffic_allocation: number
  type: ExperimentType
  workspace_id: string
  audience_rules?: AudienceRules
  page_rules?: PageRules
  redirect_settings?: RedirectSettings
}

export type CreateExperimentProps = Partial<CreateExperimentParams>

type CreateExperimentResponse = {
  status: number
  message: string
  experiment: Experiment
}

type ErrorData = {
  error: string
  message: string
  statusCode: number
}

type ErrorResponse = {
  data: ErrorData
  status: number
}

type CreateExperimentError = {
  message: Array<string> | string
  statusCode: number
  error: string
  code: string
  response?: ErrorResponse
}

export type UseCreateExperimentType = UseMutateAsyncFunction<Experiment, unknown, CreateExperimentProps, unknown>

const useCreateExperiment = (): UseCreateExperimentType => {

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { axiosApiInstance } = useAxios()

  const { objectives } = useObjectives()

  const { mutateAsync: createExperimentMutation } = useMutation({
    mutationFn: async (params: CreateExperimentProps) => {

      const workspaceId = await StorageService.getStorageItem<string>(SELECTED_WORKSPACE_KEY)
      if (!workspaceId) {
        throw new Error("No workspace found.")
      }

      const objectiveId = objectives.length ? objectives[0].id : 1

      const defaultNewExperimentValues: CreateExperimentProps = {
        "activation_event": "page_load",
        "hypothesis": "",
        // name of the experiment will be ignored initially, experiment name will be determined on the backend api
        "name": ``,
        "objective_id": Number(objectiveId),
        "status": ExperimentStatus.DRAFT,
        "traffic_allocation": 100,
        "type": ExperimentType.AB,
        "workspace_id": workspaceId,
        "audience_rules": {},
        "page_rules": {}
      }
      // let's force the creation of new experiment to always be a draft.
      const experimentParameters = { ...defaultNewExperimentValues, ...params, status: ExperimentStatus.DRAFT }

      const response = await axiosApiInstance.post<CreateExperimentResponse>(`/experiment/experiment`, experimentParameters)
      return response.data.experiment
    },
    onSuccess: async (experiment: Experiment) => {
      await registerCreatedExperiment(queryClient, experiment)
      navigate(appRoutes.experiments.setup(experiment.id))
    },
    onError: async (error: CreateExperimentError) => {
      if (error.response?.data?.message) {
        TriggerToast(error.response.data.message, undefined, 'error');
      } else {
        TriggerToast(error.message, undefined, 'error');
      }
    }
  })

  return createExperimentMutation
}

export default useCreateExperiment
